import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';
import { getUnreadCount, postMarkConversationRead, postUnreadKey } from '../util/api';
import { registerTwinrPushToken } from '../util/twinr';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo, logout } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil, types as sdkTypes } from '../util/sdkLoader';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const UNREAD_MESSAGES_UPDATED = 'app/user/UNREAD_MESSAGES_UPDATED';
export const UNREAD_KEY_RECEIVED = 'app/user/UNREAD_KEY_RECEIVED';
export const UNREAD_COUNT_RECEIVED = 'app/user/UNREAD_COUNT_RECEIVED';
export const UNREAD_LIST_REFRESH_REQUEST = 'app/user/UNREAD_LIST_REFRESH_REQUEST';
export const UNREAD_LIST_REFRESH_DONE = 'app/user/UNREAD_LIST_REFRESH_DONE';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Unread messages (P25028-128) ================ //

// Our server keeps, in each user's privateData[UNREAD_MESSAGES_KEY], the messages they have
// not opened yet (see server/api-util/unreadMessages.js for the record's shape). Only the
// server writes it. The browser reads it from the current user it already has.
export const UNREAD_MESSAGES_KEY = 'unreadMessages';

// The badge number itself is asked from our own server on every page change and tab return
// (fetchUnreadCount), which costs no Sharetribe request. The browser proves who it is with
// this key from the user's private data (see server/api-util/unreadCount.js).
export const UNREAD_KEY_FIELD = 'unreadMessagesKey';

// Added to a conversation link by the envelope list. TransactionPage then opens at the first
// unread message (the same as a link without a hash).
export const LATEST_MESSAGE_HASH = '#latest-message';

// A push link ends with #msg-<messageId> (server/pushEventsJob.js), the id of the message in
// the list (ActivityFeed), so TransactionPage opens at that message.
export const MESSAGE_HASH_PREFIX = '#msg-';

/**
 * The current user's unread messages, always with both fields.
 *
 * @returns {{ total: number, conversations: Array }} conversations newest first, each
 *   { txId, role: 'customer' | 'provider', count, lastMessageAt, preview, senderName,
 *   senderInitials, senderImage, listingTitle }
 */
const NO_CONVERSATIONS = [];
export const getUnreadMessages = currentUser => {
  const saved = currentUser?.attributes?.profile?.privateData?.[UNREAD_MESSAGES_KEY];
  // The same array each time when nothing changed, so connected pages do not re-render.
  const conversations = Array.isArray(saved?.conversations)
    ? saved.conversations
    : NO_CONVERSATIONS;
  const total = conversations.reduce((sum, c) => sum + (c.count || 0), 0);
  return { total, conversations };
};

const toTime = value => (value ? new Date(value).getTime() : 0);

/**
 * When our server last saved this conversation as read (an ISO string), or null for never.
 *
 * @param {Object} currentUser
 * @param {String} txId transaction uuid
 */
export const getReadAt = (currentUser, txId) =>
  currentUser?.attributes?.profile?.privateData?.[UNREAD_MESSAGES_KEY]?.readAt?.[txId] || null;

/**
 * True when `time` (of a message the page shows) is newer than the moment our server last
 * saved this conversation as read. The page then saves "read" even for a message our server
 * has not counted yet, so no app push is sent for it (server/pushEventsJob.js only waits a
 * few seconds).
 *
 * @param {Object} currentUser
 * @param {String} txId transaction uuid
 * @param {Date|String|number} time
 */
export const isNewerThanReadAt = (currentUser, txId, time) =>
  !!time && toTime(time) > toTime(getReadAt(currentUser, txId));

const savedUnreadUpdatedAt = currentUser =>
  currentUser?.attributes?.profile?.privateData?.[UNREAD_MESSAGES_KEY]?.updatedAt || null;

/**
 * True when our server reports a newer count than the list of messages the browser has,
 * i.e. messages arrived since the current user was loaded. Opening the envelope then loads
 * the list again (refreshUnreadListIfStale).
 *
 * @param {Object} userState state.user
 */
export const isUnreadListStale = userState => {
  const { unreadCount, currentUser } = userState;
  return !!unreadCount && toTime(unreadCount.updatedAt) > toTime(savedUnreadUpdatedAt(currentUser));
};

let lastBadge = { total: 0, conversations: NO_CONVERSATIONS };
/**
 * What the envelope shows: the newest known total (our server's count when it is newer
 * than the list) and the list of conversations. The same object while nothing changed.
 *
 * @param {Object} userState state.user
 */
export const getUnreadBadge = userState => {
  const { total: listTotal, conversations } = getUnreadMessages(userState.currentUser);
  const total = isUnreadListStale(userState) ? userState.unreadCount.total : listTotal;
  if (lastBadge.total !== total || lastBadge.conversations !== conversations) {
    lastBadge = { total, conversations };
  }
  return lastBadge;
};

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  // Unread total from our own server (P25028-128): { total, updatedAt } or null.
  unreadCount: null,
  unreadListRefreshInProgress: false,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

// Returns state with one key of the current user's private data replaced.
const withPrivateData = (state, key, value) => {
  const { currentUser } = state;
  if (!currentUser) {
    return state;
  }
  const profile = currentUser.attributes.profile || {};
  const privateData = { ...profile.privateData, [key]: value };
  return {
    ...state,
    currentUser: {
      ...currentUser,
      attributes: { ...currentUser.attributes, profile: { ...profile, privateData } },
    },
  };
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        unreadCount: null,
        unreadListRefreshInProgress: false,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case UNREAD_MESSAGES_UPDATED:
      return withPrivateData(state, UNREAD_MESSAGES_KEY, payload);
    case UNREAD_KEY_RECEIVED:
      return withPrivateData(state, UNREAD_KEY_FIELD, payload);
    case UNREAD_COUNT_RECEIVED:
      return { ...state, unreadCount: payload };
    case UNREAD_LIST_REFRESH_REQUEST:
      return { ...state, unreadListRefreshInProgress: true };
    case UNREAD_LIST_REFRESH_DONE:
      return { ...state, unreadListRefreshInProgress: false };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

export const unreadMessagesUpdated = unreadMessages => ({
  type: UNREAD_MESSAGES_UPDATED,
  payload: unreadMessages,
});

export const unreadKeyReceived = key => ({ type: UNREAD_KEY_RECEIVED, payload: key });

export const unreadCountReceived = count => ({ type: UNREAD_COUNT_RECEIVED, payload: count });

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

/**
 * Marks every message in a conversation as read (P25028-128). Called by TransactionPage
 * whenever it shows a conversation, however the user got there. Opening the inbox list
 * does not call this.
 *
 * It only asks the server when there is something to clear: the conversation is in the
 * list, or our server reports newer messages than the list holds (they may be in this
 * conversation). Only messages up to `readUpTo` count as read: a newer one the page has
 * not shown stays unread. The badge drops at once; the server's answer then replaces the
 * record.
 *
 * @param {String} txId transaction uuid
 * @param {Date|String} readUpTo createdAt of the newest message on the page
 */
export const markConversationRead = (txId, readUpTo) => (dispatch, getState) => {
  const userState = getState().user;
  const saved = userState.currentUser?.attributes?.profile?.privateData?.[UNREAD_MESSAGES_KEY];
  const { conversations } = getUnreadMessages(userState.currentUser);
  const opened = conversations.find(c => c.txId === txId);
  const notSavedYet = isNewerThanReadAt(userState.currentUser, txId, readUpTo);
  if (!txId || (!opened && !isUnreadListStale(userState) && !notSavedYet)) {
    return Promise.resolve();
  }

  const seenAll = opened && (!readUpTo || toTime(opened.lastMessageAt) <= toTime(readUpTo));
  if (seenAll) {
    const remaining = conversations.filter(c => c.txId !== txId);
    const total = remaining.reduce((sum, c) => sum + (c.count || 0), 0);
    dispatch(unreadMessagesUpdated({ ...saved, conversations: remaining, total }));
    if (userState.unreadCount) {
      const { total: countTotal, updatedAt } = userState.unreadCount;
      dispatch(unreadCountReceived({ total: Math.max(0, countTotal - opened.count), updatedAt }));
    }
  }

  const readUpToMaybe = readUpTo ? { readUpTo: new Date(readUpTo).toISOString() } : {};
  return postMarkConversationRead({ transactionId: txId, ...readUpToMaybe })
    .then(response => {
      const record = response?.[UNREAD_MESSAGES_KEY];
      if (record) {
        dispatch(unreadMessagesUpdated(record));
        dispatch(unreadCountReceived({ total: record.total, updatedAt: record.updatedAt }));
      }
    })
    .catch(e => log.error(e, 'mark-conversation-read-failed', { txId }));
};

// Asked once per signed-in user and page load at most, so a failing server cannot cause a
// stream of key requests.
let unreadKeyRequestedFor = null;

const requestUnreadKey = (dispatch, userId) => {
  if (unreadKeyRequestedFor === userId) {
    return Promise.resolve(null);
  }
  unreadKeyRequestedFor = userId;
  return postUnreadKey().then(response => {
    if (response?.key) {
      dispatch(unreadKeyReceived(response.key));
    }
    return response?.key || null;
  });
};

let unreadCountInProgress = false;
// A page change while a check is running asks for one more check afterwards, so it is not
// lost.
let unreadCountAgain = false;

/**
 * Asks our own server for the unread total (P25028-128). Called by the Topbar on every
 * page change and when the tab comes back into view. It costs no Sharetribe request, so it
 * can run that often. The first time, it gets the user's key (one call per user, ever).
 */
export const fetchUnreadCount = () => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (!currentUser) {
    return Promise.resolve();
  }
  if (unreadCountInProgress) {
    unreadCountAgain = true;
    return Promise.resolve();
  }
  const userId = currentUser.id.uuid;
  const savedKey = currentUser.attributes?.profile?.privateData?.[UNREAD_KEY_FIELD];

  unreadCountInProgress = true;
  const withKey = savedKey ? Promise.resolve(savedKey) : requestUnreadKey(dispatch, userId);
  return withKey
    .then(key => key && getUnreadCount(key))
    .catch(e => {
      // The key is not known to this server's database (for example a new database): make
      // a new link once, then ask again.
      if (e?.unknownKey) {
        return requestUnreadKey(dispatch, userId).then(key => key && getUnreadCount(key));
      }
      throw e;
    })
    .then(count => {
      if (count && typeof count.total === 'number') {
        dispatch(unreadCountReceived({ total: count.total, updatedAt: count.updatedAt }));
      }
    })
    .catch(e => log.error(e, 'fetch-unread-count-failed'))
    .finally(() => {
      unreadCountInProgress = false;
      if (unreadCountAgain) {
        unreadCountAgain = false;
        dispatch(fetchUnreadCount());
      }
    });
};

// The parameters fetchCurrentUser uses by default, so a refresh returns the same data.
export const currentUserShowParams = () => ({
  include: ['profileImage', 'stripeAccount'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.square-xsmall',
    'variants.square-xsmall2x',
  ],
  'imageVariant.square-xsmall': sdkUtil.objectQueryString({
    w: 40,
    h: 40,
    fit: 'crop',
  }),
  'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
    w: 80,
    h: 80,
    fit: 'crop',
  }),
});

// ================ Blocked users (P25028-272) ================ //

// The ids in the signed-in user's own block list, kept in their private data.
export const blockedUserIdsOf = currentUser => {
  const ids = currentUser?.attributes?.profile?.privateData?.blockedUserIds;
  return Array.isArray(ids) ? ids.filter(id => typeof id === 'string') : [];
};

/**
 * Adds `userId` to the signed-in user's block list (`block: true`) or removes it.
 *
 * The list is read fresh first, so a block made in another tab or in the app is not
 * lost when this one writes the whole list back. The updated user then replaces the
 * one in the store, so every page sees the change at once.
 */
export const updateBlockedUser = ({ userId, block }) => (dispatch, getState, sdk) =>
  sdk.currentUser
    .show()
    .then(response => {
      const [freshUser] = denormalisedResponseEntities(response);
      const current = blockedUserIdsOf(freshUser).filter(id => id !== userId);
      const blockedUserIds = block ? [...current, userId] : current;
      return sdk.currentUser.updateProfile(
        { privateData: { blockedUserIds } },
        { expand: true, ...currentUserShowParams() }
      );
    })
    .then(response => {
      const [user] = denormalisedResponseEntities(response);
      if (user) {
        dispatch(currentUserShowSuccess(user));
      }
      return user;
    });

// Public profiles for the "Blocked users" page. An account that no longer exists is
// left out.
export const fetchUsersByIds = userIds => (dispatch, getState, sdk) =>
  Promise.all(
    userIds.map(id =>
      sdk.users
        .show({
          id: new sdkTypes.UUID(id),
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        })
        .then(response => denormalisedResponseEntities(response)[0] || null)
        .catch(() => null)
    )
  ).then(users => users.filter(Boolean));

/**
 * Loads the current user again when the envelope is opened and our server reports newer
 * messages than the list holds (P25028-128), so the list matches the number on the badge.
 *
 * This is one Sharetribe request, and only when someone opens the envelope after new
 * messages arrived. Opening it with an up-to-date list makes no request.
 */
export const refreshUnreadListIfStale = () => (dispatch, getState, sdk) => {
  const userState = getState().user;
  if (
    !userState.currentUser ||
    userState.unreadListRefreshInProgress ||
    !isUnreadListStale(userState)
  ) {
    return Promise.resolve();
  }

  dispatch({ type: UNREAD_LIST_REFRESH_REQUEST });
  return sdk.currentUser
    .show(currentUserShowParams())
    .then(response => {
      const [freshUser] = denormalisedResponseEntities(response);
      if (freshUser) {
        dispatch(currentUserShowSuccess(freshUser));
      }
    })
    .catch(e => log.error(e, 'refresh-unread-list-failed'))
    .finally(() => dispatch({ type: UNREAD_LIST_REFRESH_DONE }));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || currentUserShowParams();

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // The account was deleted (P25028-266) or banned, most often from another browser or
      // the app while this one stayed signed in. Sharetribe still answers, with an empty
      // user, so without this the topbar showed a blank "signed in" avatar. Sign out here.
      const { deleted, banned } = currentUser.attributes || {};
      if (deleted || banned) {
        dispatch(currentUserShowSuccess(null));
        return dispatch(logout())
          .catch(() => null)
          .then(() => null);
      }

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      if (!currentUser) {
        return;
      }
      dispatch(fetchCurrentUserHasListings());
      // Inside the Twinr app, save this phone's push token. Not awaited: it may wait ~2 s
      // for the bridge, and it must never slow down or break the page.
      registerTwinrPushToken(currentUser.id.uuid).catch(() => {});
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
